import React, { createContext, useContext, useState, useEffect } from 'react';
import { clientApi } from '../api/clientApi';
import { useAuth } from './AuthContext';

const ClientContext = createContext();

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

export const ClientProvider = ({ children }) => {
  const { user } = useAuth();
  // The clients list is sourced from GET /moderators/, which is an admin-only
  // endpoint. Firing it for a moderator/subscriber returns 401 and trips the
  // adminApiClient interceptor (wipes token + hard-redirects to /login), which
  // makes non-admin sessions impossible. Only admins should fetch it.
  const isAdmin = user?.role === 'admin';

  const [currentClient, setCurrentClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch clients list (for manual Refresh; initial load is in useEffect)
  const fetchClients = async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const response = await clientApi.getClients();
      const clientsList = Array.isArray(response) ? response : response.data || [];
      setClients(clientsList);
      setCurrentClient(prev => {
        if (prev || clientsList.length === 0) return prev;
        const active = clientsList.find(c => c.is_active === 'true' || c.is_active === true);
        return active || prev;
      });
    } catch (err) {
      const status = err.response?.status;
      if (status === 403 || status === 401) {
        setClients([]);
        setError(null);
      } else {
        setError(err.message || 'Failed to fetch clients');
        console.error('Error fetching clients:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  // Set current client
  const selectClient = (client) => {
    setCurrentClient(client);
    // Store in localStorage for persistence
    localStorage.setItem('selectedClient', JSON.stringify(client));
  };

  // Get current client API key
  const getCurrentClientApiKey = () => {
    return currentClient?.api_key || null;
  };

  // Load client from localStorage on mount
  useEffect(() => {
    const savedClient = localStorage.getItem('selectedClient');
    if (savedClient) {
      try {
        const client = JSON.parse(savedClient);
        setCurrentClient(client);
      } catch (err) {
        console.error('Error parsing saved client:', err);
        localStorage.removeItem('selectedClient');
      }
    }
  }, []);

  // Fetch clients on mount (single source; AbortController avoids double call in Strict Mode).
  // Admin-only: GET /moderators/ rejects non-admin JWTs (see isAdmin note above).
  useEffect(() => {
    if (!isAdmin) {
      setClients([]);
      return;
    }
    const ac = new AbortController();
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await clientApi.getClients(ac.signal);
        if (cancelled) return;
        const clientsList = Array.isArray(response) ? response : response?.data || [];
        setClients(clientsList);
        if (clientsList.length > 0) {
          setCurrentClient((prev) => {
            if (prev) return prev;
            const activeClient = clientsList.find((client) => client.is_active === 'true' || client.is_active === true);
            return activeClient || prev;
          });
        }
      } catch (err) {
        if (cancelled || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
        const status = err.response?.status;
        if (status === 403 || status === 401) {
          setClients([]);
          setError(null);
        } else {
          setError(err.message || 'Failed to fetch clients');
          console.error('Error fetching clients:', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [isAdmin]);

  const value = {
    currentClient,
    clients,
    loading,
    error,
    selectClient,
    getCurrentClientApiKey,
    fetchClients
  };

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
};
